import type { StateAction } from "@entrilia/shared/types";
import type { AuthChallengeOutcome } from "../../api/types/accessCheckApiTypes";
import type { BucketClassification, ConnectionCheckResult } from "./types";

export type AccessCheckStepKind =
  | "initial"
  | "running-checks"
  | "collecting-email"
  | "requesting-code"
  | "entering-code"
  | "verifying"
  | "show-results"
  | "diagnostics-failed";

export type VerifyResult =
  { kind: "valid"; hasAccess: boolean } | { kind: "invalid"; outcome: AuthChallengeOutcome } | { kind: "error" };

export interface AccessCheckState {
  step: AccessCheckStepKind;
  checks: ConnectionCheckResult[];
  email: string;
  emailDraft: string;
  emailFieldError?: string;
  classification?: BucketClassification;
  hasInvestorPortalAccess?: boolean;
  resending: boolean;
  submitting: boolean;
  codeError?: string;
  verifyDone: boolean;
  verifyResult?: VerifyResult;
}

export const initialAccessCheckState: AccessCheckState = {
  step: "initial",
  checks: [],
  email: "",
  emailDraft: "",
  emailFieldError: undefined,
  classification: undefined,
  hasInvestorPortalAccess: undefined,
  resending: false,
  submitting: false,
  codeError: undefined,
  verifyDone: false,
  verifyResult: undefined,
};

export const startChecksAction: StateAction<AccessCheckState> = (state) => ({
  ...state,
  step: "running-checks",
});

export const showResultsAction =
  (
    checks: ConnectionCheckResult[],
    classification: BucketClassification,
    extras?: { email?: string; hasInvestorPortalAccess?: boolean }
  ): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    step: "show-results",
    checks,
    classification,
    email: extras?.email ?? state.email,
    hasInvestorPortalAccess: extras?.hasInvestorPortalAccess,
  });

export const collectingEmailAction =
  (checks: ConnectionCheckResult[]): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    step: "collecting-email",
    checks,
  });

export const updateEmailDraftAction =
  (value: string): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    emailDraft: value,
    emailFieldError: undefined,
  });

export const setEmailFieldErrorAction =
  (error: string): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    emailFieldError: error,
  });

export const requestingCodeAction =
  (checks: ConnectionCheckResult[], email: string): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    step: "requesting-code",
    checks,
    email,
  });

export const enteringCodeAction =
  (codeError?: string): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    step: "entering-code",
    resending: false,
    submitting: false,
    codeError,
  });

export const requestCodeFailedAction =
  (checks: ConnectionCheckResult[], email: string, message: string): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    step: "collecting-email",
    checks,
    emailDraft: email,
    emailFieldError: message,
  });

export const startResendingAction: StateAction<AccessCheckState> = (state) => ({
  ...state,
  resending: true,
  codeError: undefined,
});

export const finishResendingAction =
  (codeError?: string): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    resending: false,
    codeError,
  });

export const startVerifyingAction: StateAction<AccessCheckState> = (state) => ({
  ...state,
  step: "verifying",
  verifyDone: false,
  verifyResult: undefined,
  submitting: true,
});

export const finishVerifyingAction =
  (verifyResult: VerifyResult, blobChecks?: ConnectionCheckResult[]): StateAction<AccessCheckState> =>
  (state) => ({
    ...state,
    verifyDone: true,
    verifyResult,
    checks: blobChecks ? [...state.checks, ...blobChecks] : state.checks,
  });

export const diagnosticsFailedAction: StateAction<AccessCheckState> = (state) => ({
  ...state,
  step: "diagnostics-failed",
});

export const resetAccessCheckAction: StateAction<AccessCheckState> = () => initialAccessCheckState;
